import { useCallback, useEffect, useRef } from 'react';

/**
 * Crash/refresh protection for the RIS patient registration form (ported from
 * Health Hub's healthhub-frontend/src/hooks/useRegistrationDraft.js). The
 * form holds every answer in React state until the final PATCH, so a reload
 * used to throw the lot away.
 *
 * This hook only ever *writes*. It never restores anything and exposes no UI
 * — restoring is an office-assistant action taken from the Location modal
 * against a named patient (see components/customComponents/LocationModal.js
 * and the staff-only GET /draft in routes/PatientRegistrationReferredRoute.js),
 * never something the form does on its own, since the registration kiosk is a
 * shared device.
 *
 * The draft lives server-side (ris_registration_drafts) rather than in
 * browser storage so it survives a closed tab or a device switch, and so the
 * PHI in it sits behind the same auth as the rest of the chart.
 *
 * Signatures are never written to a draft (see SIGNATURE_FIELDS). They are
 * legal attestations, and silently restoring one would have the form claim a
 * signature the patient did not give on this attempt. Anyone who resumes a
 * draft signs again.
 */

const SAVE_DEBOUNCE_MS = 1200;

const SIGNATURE_FIELDS = [
  'hipaaSigned',
  'hipaa_signature',
  'hipaa_signature_image',
  'hipaa_signature_timestamp',
  'patient_signature_image',
  'patient_signature_timestamp',
  'insured_signature_image',
  'insured_signature_timestamp',
  'tempDemographics',
];

/** Strips the fields a draft must not carry. Exported for the restore side. */
export const stripSignatures = (patientData) => {
  const safe = { ...(patientData || {}) };
  SIGNATURE_FIELDS.forEach((field) => delete safe[field]);
  return safe;
};

/**
 * @param {object}  options
 * @param {string}  options.risPatientsId  Scopes the draft to one patient.
 * @param {object}  options.patientData    The live form state.
 * @param {boolean} options.enabled        Held false until the server prefill
 *                                         has settled; otherwise the first
 *                                         write would capture an empty form
 *                                         and overwrite a perfectly good draft.
 */
export default function useRegistrationDraft({ risPatientsId, patientData, enabled }) {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const lastSavedRef = useRef(null);
  const payloadRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const canSave = Boolean(enabled && risPatientsId);

  const payload = JSON.stringify(stripSignatures(patientData));
  payloadRef.current = payload;

  const save = useCallback(() => {
    if (!canSave || stoppedRef.current) return;
    if (payloadRef.current === lastSavedRef.current) return;

    const body = payloadRef.current;
    // Claimed before the request resolves, on purpose: if it fails we do not
    // want a retry storm on every keystroke, and the next real edit will
    // produce a different payload and try again anyway.
    lastSavedRef.current = body;

    // Autosave is a safety net, never a prerequisite. A failed save must not
    // interrupt someone mid-form with an error they cannot act on.
    fetch(`${BASE_URL}/patientRegistrationReferred/draft`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ris_patients_id: risPatientsId, payload: JSON.parse(body) }),
    }).catch(() => {});
  }, [BASE_URL, canSave, risPatientsId]);

  // Seed the baseline the moment saving opens up, so a prefilled-but-untouched
  // form does not count as a change worth writing.
  useEffect(() => {
    if (canSave && lastSavedRef.current === null) {
      lastSavedRef.current = payloadRef.current;
    }
  }, [canSave]);

  useEffect(() => {
    if (!canSave) return undefined;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [canSave, payload, save]);

  // The debounce window is exactly when a reload hurts most, so commit
  // immediately when the page is going away or being backgrounded. RIS's
  // fetch is wrapped to attach a Firebase bearer token asynchronously (see
  // utils/apiClient.js), so unlike Health Hub's keepalive/nativeFetch
  // approach this is a best-effort flush rather than a guaranteed one — the
  // 1200ms debounce already keeps most edits saved well before a tab closes.
  useEffect(() => {
    if (!canSave) return undefined;
    const onHidden = () => {
      if (document.visibilityState === 'hidden') save();
    };
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('pagehide', save);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [canSave, save]);

  /**
   * Stops this hook from writing again. Called once the registration has
   * committed — the server drops the draft row inside that same request, and
   * without this a trailing debounce would immediately recreate it.
   */
  const stopSaving = useCallback(() => {
    stoppedRef.current = true;
    clearTimeout(timerRef.current);
  }, []);

  return { stopSaving };
}
